// stores/tablePrefilter.ts
import { defineStore } from 'pinia';

// Maps tabIndex -> selectedOptionIndex for active tabs
export type ActiveTabsMap = Record<number, number>;

export const useTablePrefilterStore = defineStore('tablePrefilterStore', {
  state: (): {
    activeTabsMapByKey: Record<string, ActiveTabsMap>;
    // Backward compatibility fields for non-paginated DataTable
    selectedTabByKey: Record<string, number>;
    selectedOptionIndexByKey: Record<string, number[]>;
  } => ({
    activeTabsMapByKey: {}, // Maps dataKey -> { tabIndex: selectedOptionIndex, ... }
    // Backward compatibility
    selectedTabByKey: {},
    selectedOptionIndexByKey: {},
  }),

  actions: {
    /**
     * Set the active tabs and their selected options for a given dataKey (paginated)
     */
    setActiveTabsMap(dataKey: string, activeTabsMap: ActiveTabsMap) {
      this.activeTabsMapByKey[dataKey] = activeTabsMap;
    },

    /**
     * Get the active tabs map for a given dataKey (paginated)
     */
    getActiveTabsMap(dataKey: string): ActiveTabsMap {
      return this.activeTabsMapByKey[dataKey] ?? {};
    },

    /**
     * Clear all prefilter state for a given dataKey
     */
    clearPrefilterState(dataKey: string) {
      delete this.activeTabsMapByKey[dataKey];
      delete this.selectedTabByKey[dataKey];
      delete this.selectedOptionIndexByKey[dataKey];
    },

    // ========== Backward compatibility methods for non-paginated DataTable ==========
    /**
     * @deprecated Use setActiveTabsMap() for new paginated components
     */
    setSelectedTab(dataKey: string, tabIndex: number) {
      this.selectedTabByKey[dataKey] = tabIndex;
    },

    /**
     * @deprecated Use getActiveTabsMap() for new paginated components
     */
    getSelectedTab(dataKey: string): number {
      return this.selectedTabByKey[dataKey] ?? 0;
    },

    /**
     * @deprecated Use setActiveTabsMap() for new paginated components
     */
    setOptionIndices(dataKey: string, optionIndices: number[]) {
      this.selectedOptionIndexByKey[dataKey] = optionIndices;
    },

    /**
     * @deprecated Use getActiveTabsMap() for new paginated components
     */
    getOptionIndices(dataKey: string): number[] {
      return this.selectedOptionIndexByKey[dataKey] ?? [];
    },
  },

  persist: {
    storage: localStorage,
  },
});
